import React, { useEffect, useRef, useState } from 'react';
import type { ChatResponseAPI, SourceChunk } from '../types';
import type { ApiMode } from './ChatInput';

interface HistoryItem {
  query:    string;
  response: ChatResponseAPI | string;
  mode:     ApiMode;
}

interface ChatResponseProps {
  history:      HistoryItem[];
  pendingQuery: string | null;
  apiMode:      ApiMode;
}

/* ── SQL result box — resizable, monospace ── */
const SqlResult: React.FC<{ sql: string }> = ({ sql }) => {
  const [height, setHeight] = useState(220);
  const dragging  = useRef(false);
  const startY    = useRef(0);
  const startH    = useRef(0);
  const [copied, setCopied]   = useState(false);

  const onMouseDown = (e: React.MouseEvent) => {
    dragging.current = true;
    startY.current   = e.clientY;
    startH.current   = height;
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      const delta = e.clientY - startY.current;
      setHeight(Math.max(80, startH.current + delta));
    };
    const onUp = () => { dragging.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  const copy = () => {
    navigator.clipboard.writeText(sql);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div style={{ position: 'relative', marginTop: 6 }}>
      <button
        onClick={copy}
        style={{
          position: 'absolute', top: 8, right: 10, zIndex: 2,
          background: '#1e293b', border: '1px solid #334155',
          borderRadius: 4, padding: '2px 8px', fontSize: 10,
          color: copied ? '#4ade80' : '#94a3b8', cursor: 'pointer',
        }}
      >
        {copied ? '✓ Copied' : 'Copy'}
      </button>
      <pre
        style={{
          height, overflow: 'auto', margin: 0,
          background: '#020817', border: '1px solid #1e3a5f',
          borderRadius: 8, padding: '10px 14px',
          fontSize: 12, lineHeight: 1.65, color: '#7dd3fc',
          fontFamily: "'JetBrains Mono','Fira Code',monospace",
          whiteSpace: 'pre-wrap', wordBreak: 'break-all',
          scrollbarWidth: 'thin', scrollbarColor: '#334155 #020817',
        } as React.CSSProperties}
      >
        {sql}
      </pre>
      {/* drag handle */}
      <div
        onMouseDown={onMouseDown}
        style={{
          height: 8, background: '#1e293b', borderRadius: '0 0 8px 8px',
          cursor: 'ns-resize', display: 'flex', alignItems: 'center',
          justifyContent: 'center', userSelect: 'none',
        }}
      >
        <div style={{ width: 32, height: 2, borderRadius: 1, background: '#334155' }} />
      </div>
    </div>
  );
};

/* ── AI result box — resizable, small ── */
const AiResult: React.FC<{ text: string }> = ({ text }) => {
  const [height, setHeight] = useState(80);
  const dragging = useRef(false);
  const startY   = useRef(0);
  const startH   = useRef(0);

  const onMouseDown = (e: React.MouseEvent) => {
    dragging.current = true; startY.current = e.clientY; startH.current = height; e.preventDefault();
  };
  useEffect(() => {
    const onMove = (e: MouseEvent) => { if (dragging.current) setHeight(Math.max(40, startH.current + e.clientY - startY.current)); };
    const onUp   = () => { dragging.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  return (
    <div style={{ marginTop: 6 }}>
      <div style={{
        height, overflow: 'auto', background: '#0f172a',
        border: '1px solid #f59e0b44', borderRadius: 8,
        padding: '8px 12px', fontSize: 13, color: '#cbd5e1', lineHeight: 1.6,
        scrollbarWidth: 'thin', scrollbarColor: '#334155 #0f172a',
      } as React.CSSProperties}>
        {text}
      </div>
      <div onMouseDown={onMouseDown}
        style={{ height: 8, background: '#1e293b', borderRadius: '0 0 8px 8px', cursor: 'ns-resize', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 32, height: 2, borderRadius: 1, background: '#334155' }} />
      </div>
    </div>
  );
};


/* ── Main ChatResponse ── */
const ChatResponse: React.FC<ChatResponseProps> = ({ history, pendingQuery, apiMode }) => {
  const scrollEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history, pendingQuery]);

  const handleDownload = async (fileName: string) => {
    try {
       const response = await fetch(`https://forgerag.com/api/files/download/${(getFileName(fileName))}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = getFileName(fileName) as string; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error('Download failed', err); }
  };

  const getFileName = (file_name: string) => file_name.split('/').pop();

  if (!history.length && !pendingQuery) {
    return <div className="chat-response">Ask a question to get started.</div>;
  }

  return (
    <div className="chat-response">
      {history.map((item, i) => (
        <div key={i} className="message">
          <div><strong>&gt;</strong> {item.query}</div>

          {/* ── RAG response (unchanged layout) ── */}
          {item.mode === 'rag' && typeof item.response !== 'string' && (
            <>
              <div className="answer">{(item.response as ChatResponseAPI).answer}</div>
              <div className="sources-title">📚 Sources:</div>
              {(item.response as ChatResponseAPI).sources.slice(0, 2).map((chunk: SourceChunk, j: number) => (
                <div key={j} className="source">
                  <div className="source-header">
                    📄 <strong>
                      <a href="#" onClick={(e) => { e.preventDefault(); handleDownload(chunk.file_name); }}>
                        {getFileName(chunk.file_name)}
                      </a>
                    </strong>
                    <span className="source-meta">&nbsp;&nbsp;Score {chunk.score.toFixed(2)}</span>
                    <span className="source-meta1">Page {chunk.page}.</span>
                  </div>
                  <div className="source-content">{chunk.content.substring(0, 500)}…</div>
                </div>
              ))}
            </>
          )}

          {/* ── SQL response — resizable code block ── */}
          {item.mode === 'sql' && (
            <>
              <div className="answer" style={{ color: '#34d399', fontSize: 12, marginBottom: 2 }}>
                Generated SQL
              </div>
              <SqlResult sql={typeof item.response === 'string' ? item.response : JSON.stringify(item.response)} />
            </>
          )}

          {/* ── AI / under-dev response ── */}
          {item.mode === 'ai' && (
            <AiResult text={typeof item.response === 'string' ? item.response : JSON.stringify(item.response)} />
          )}
        </div>
      ))}

      {/* pending loader */}
      {pendingQuery && (
        <div className="message pending-message">
          <div><strong>&gt;</strong> {pendingQuery}</div>
          <div className="answer loading-dots">
            {apiMode === 'sql' ? 'Generating SQL' : 'Searching'}<span>.</span><span>.</span><span>.</span>
          </div>
        </div>
      )}

      <div ref={scrollEndRef} />
    </div>
  );
};

export default ChatResponse;
